import { FC, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { faWindowRestore } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useElementSize, useHash, useMediaQuery } from "@mantine/hooks";
import { usePrefab } from "@prefab-cloud/prefab-cloud-react";

import { IssueStatus, Severity } from "@protos/common/v1/common";
import { Issue as IssueProto, IssueType } from "@protos/issues/v1/issues";
import {
  CodeLocationIconList,
  OptionalAnchor,
  SeverityCardTimestamp,
} from "@shared/components";
import { useBasePath } from "@shared/hooks";
import {
  formatFilePath,
  getSanitizedUrl,
  getSeverityColorScheme,
  pathNeedsFormatting,
  SECRETS_AI_RULESET,
} from "@shared/utils";

import { getGitProviderLinks } from "../../utils";
import { RuleGroupAutotriage } from "../RuleGroupAutotriage";

import styles from "./RuleGroupFinding.module.css";

const DETAILS_LINK_MAP: Record<IssueType, string | null> = {
  [IssueType.SCA]: "/supply-chain/findings",
  [IssueType.SAST]: "/findings",
  [IssueType.SECRETS]: "/secrets",
  [IssueType.RESEARCH]: null,
  [IssueType.UNSPECIFIED]: null,
};

export interface RuleGroupFindingProps {
  issue: IssueProto;
  issueType: IssueType;
  hasComments: boolean;
  allRefs: string[];
  severity: Severity;
  selectedFindings: number[];
  onCheck: (issue: IssueProto, checked: boolean, shiftKey: boolean) => void;
  allGroupFindingsSelected: boolean;
}

export const RuleGroupFinding: FC<RuleGroupFindingProps> = ({
  issue,
  issueType,
  hasComments,
  allRefs,
  severity,
  selectedFindings,
  onCheck,
  allGroupFindingsSelected,
}) => {
  const { get: getPrefabVal } = usePrefab();
  const turnOffLockfileUrl = getPrefabVal(
    "sca.turn_off_lockfile_links"
  ) as boolean;

  const { ref: filenameRef, width: filenameWidth } = useElementSize();
  const history = useHistory();
  const basePath = useBasePath();
  const isFixed = issue.status === IssueStatus.FIXED;
  const { colors, breakpoints } = useMantineTheme();

  const { color, backgroundShade, textShade, borderColor } =
    getSeverityColorScheme(colors, severity);
  const isLg = useMediaQuery(`(min-width: ${breakpoints.xl})`);

  const params = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return params.toString();
  }, [history.location.search]);

  const [selectedFindingId] = useHash();
  const isSelected = selectedFindingId.replace("#", "") === issue.id.toString();

  const repoUrl = useMemo(
    () => getSanitizedUrl(issue.lastSeenScan?.meta?.repo_url),
    [issue.lastSeenScan?.meta?.repo_url]
  );

  const { ref: refName, refUrl, provider } = getGitProviderLinks(issue);

  const allOtherBranchNames = useMemo(
    () => allRefs.filter((ref) => ref !== issue.ref),
    [allRefs, issue.ref]
  );
  const formattedFilePath = formatFilePath(issue.filePath ?? "", isLg ? 5 : 2);
  const displayFilePath = `${formattedFilePath}:${issue.line}`;
  const fullFilePath = `${issue.filePath ?? ""}:${issue.line}`;

  // shorten the displayed repo name since there's little room for it (if applicable, split by "/" and only keep the last part)
  let formattedRepoName = issue.repository?.name;
  if (formattedRepoName?.includes("/")) {
    const repoComponents = formattedRepoName.split("/");
    formattedRepoName = repoComponents[repoComponents.length - 1];
  }

  const showDetailsLink = [
    IssueType.SCA,
    IssueType.SAST,
    IssueType.SECRETS,
  ].includes(issueType);

  const detailsLink = `${basePath}${DETAILS_LINK_MAP[issueType]}/${issue.id}`;

  const getLineOfCodeURL = () => {
    if (issueType === IssueType.SCA) {
      if (!turnOffLockfileUrl) {
        return issue.lineOfCodeUrl;
      } else {
        return undefined;
      }
    } else {
      return issue.lineOfCodeUrl;
    }
  };

  return (
    // id on this Flex is used for the findings tour
    // do not remove or move without verifying that the tutorial still works
    // and highlights an individual finding row on step 3
    <Flex id="findings-tutorial-step-3">
      <Flex
        w={40}
        mih={35}
        direction="column"
        align="center"
        justify="start"
        style={{
          backgroundColor: color[backgroundShade],
          color: color[textShade],
          lineHeight: 1,
          flexShrink: 0,
          textAlign: "center",
          borderTop: "1px solid rgba(255,255,255,0.6)",
        }}
        p={{ base: "xs", lg: 8 }}
      >
        <Checkbox
          checked={
            allGroupFindingsSelected || selectedFindings.includes(issue.id)
          }
          onChange={(e) =>
            onCheck(
              issue,
              e.target.checked,
              (e.nativeEvent as MouseEvent).shiftKey
            )
          }
          size="sm"
          disabled={isFixed || allGroupFindingsSelected}
          styles={(theme) => ({
            input: {
              borderColor: color[backgroundShade],
              transition: "opacity 100ms ease",
              "&:checked": {
                backgroundColor: "#fff",
                borderColor: borderColor,
              },
              "&:hover": {
                cursor: "pointer",
                opacity: 0.85,
              },
            },
            icon: {
              color: `${theme.colors["dark"][7]} !important`,
            },
          })}
        />
      </Flex>
      <Box
        bg={isSelected ? "blue.1" : "#fff"}
        w="100%"
        pt={{ base: 2, xl: 6 }}
        pb={{ base: 2, xl: 6 }}
        style={{
          borderTop: `1px solid var(--mantine-color-gray-3)`,
          flexGrow: 1,
        }}
      >
        <Group wrap="nowrap" align="stretch">
          <Flex
            justify={{ base: "start", xl: "space-between" }}
            direction={{ base: "column-reverse", xl: "row" }}
            w="100%"
            gap="sm"
            pl={12}
            pt={{ base: 8, xl: 0 }}
            pb={{ base: 12, xl: 0 }}
          >
            <Group wrap="nowrap" style={{ flexGrow: 1 }}>
              {issue.relevantSince && (
                <Box
                  display={{ base: "none", xl: "block" }}
                  style={{ flexShrink: 0, flexGrow: 0 }}
                >
                  <SeverityCardTimestamp
                    date={issue.relevantSince}
                    hasComments={hasComments}
                    isBlocking={issue.isBlocking ?? false}
                    color={color[textShade]}
                    fontSize=".75rem"
                  />
                </Box>
              )}
              <Tooltip
                label={
                  pathNeedsFormatting(issue.filePath ?? "", isLg ? 5 : 2) &&
                  issue.filePath
                }
                position="top-start"
                disabled={
                  !pathNeedsFormatting(issue.filePath ?? "", isLg ? 5 : 2)
                }
                withArrow
                withinPortal
              >
                <Box
                  pb={1}
                  ref={filenameRef}
                  style={{ flexGrow: 1 }}
                  fz={14}
                  miw={80}
                >
                  <OptionalAnchor
                    url={getLineOfCodeURL()}
                    isCopyable
                    copyableText={fullFilePath}
                    copyTooltipText="Copy full path"
                    style={
                      isLg
                        ? {
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: `${Math.floor(filenameWidth - 20)}px`,
                          }
                        : undefined
                    }
                  >
                    {displayFilePath}
                  </OptionalAnchor>
                </Box>
              </Tooltip>
            </Group>

            <Flex
              gap={{ base: "m", xl: 12 }}
              pr="md"
              justify={{ base: "space-between", xl: "flex-start" }}
              align="center"
            >
              <Group gap={2}>
                {issue.createdAt && (
                  <Box display={{ base: "block", xl: "none" }}>
                    <SeverityCardTimestamp
                      date={issue.createdAt}
                      hasComments={hasComments}
                      isBlocking={issue.isBlocking ?? false}
                      color={color[textShade]}
                      fontSize=".75rem"
                    />
                  </Box>
                )}

                <CodeLocationIconList
                  repoName={formattedRepoName}
                  repoHref={repoUrl}
                  branchName={refName}
                  branchHref={refUrl}
                  allOtherBranchNames={allOtherBranchNames}
                  repoProvider={provider}
                  externalTicket={issue.externalTicket}
                  failedTicket={issue.ticketAttempts[0]}
                  columnBreakpoint="xs"
                  aiTag={issue.aiTags?.tags}
                  transitivity={
                    issue.scaAttributes?.foundDependency?.transitivity
                  }
                  reachability={issue.scaAttributes?.reachability}
                  reachableCondition={issue.scaAttributes?.reachableCondition}
                  validationState={issue.secretsAttributes?.validationState}
                  historicalInfo={issue.secretsAttributes?.historicalInfo}
                  isGenericSecret={issue.ruleset === SECRETS_AI_RULESET}
                />
              </Group>

              {/* details */}
              {showDetailsLink && (
                <Group gap={0}>
                  <Button
                    leftSection={<FontAwesomeIcon icon={faWindowRestore} />}
                    variant="subtle"
                    size="compact-sm"
                    classNames={{
                      root: styles.root,
                      section: styles.section,
                    }}
                    component={Link}
                    to={{
                      pathname: detailsLink,
                      state: { params },
                    }}
                  >
                    Details
                  </Button>
                </Group>
              )}
            </Flex>
          </Flex>
        </Group>

        {/* autotriage is only available to SAST */}
        {issueType === IssueType.SAST && (
          <RuleGroupAutotriage autotriage={issue.autotriage ?? null} />
        )}
      </Box>
    </Flex>
  );
};
